import { useEffect, useState } from "react";
import { normalizeScanStatus, type Scan } from "../api";
import { useAppState } from "../AppStateContext";

export function formatHms(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const hh = String(Math.floor(s / 3600)).padStart(2, "0");
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

/**
 * Active (non-paused) time the scan has been running, in seconds. Pure so the
 * pause/finish math can be unit-tested.
 *
 * `pausedMs` is the time already spent in finished pauses; `ongoingPauseSinceMs`
 * is the start of a pause that is still open (null while running);
 * `finishedAtMs` is the scan's completed_at once it's over. The reference point
 * is whichever of those applies — freezing it at the pause start (rather than
 * the live clock) is what stops the timer drifting during a pause and jumping
 * on resume, and freezing it at completed_at is what locks in the final total.
 */
export function elapsedActiveSeconds(opts: {
  startedAtMs: number;
  nowMs: number;
  pausedMs: number;
  ongoingPauseSinceMs: number | null;
  finishedAtMs?: number | null;
}): number {
  const reference = opts.finishedAtMs ?? opts.ongoingPauseSinceMs ?? opts.nowMs;
  return Math.max(0, Math.floor((reference - opts.startedAtMs - opts.pausedMs) / 1000));
}

/**
 * Count-up timer for a scan: starts at zero and keeps whatever it racks up —
 * there is no fixed budget the run has to fit into. Paused time is discounted
 * (tracked centrally in AppState), and a finished scan freezes on its final
 * total instead of resetting.
 *
 * Returns a formatted HH:MM:SS string (or "—" when there's nothing to show).
 * Client-side only: the paused total resets on a full page reload because the
 * backend exposes no paused-duration field.
 */
export function useScanElapsed(scan: Scan | null | undefined): string {
  const { pausedMsByScan, activePause } = useAppState();
  const status = normalizeScanStatus(scan?.status);
  const isTicking = !!scan && status === "running";
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isTicking) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [isTicking]);

  if (!scan?.created_at) return "—";
  const startedAtMs = new Date(scan.started_at ?? scan.created_at).getTime();
  if (!Number.isFinite(startedAtMs)) return "—";

  const isFinished = status === "completed" || status === "cancelled" || status === "failed";
  const finishedAtMs = scan.completed_at ? new Date(scan.completed_at).getTime() : NaN;
  // A finished scan without a usable completed_at has no total to freeze on —
  // counting against the live clock would inflate it on every reload.
  if (isFinished && !Number.isFinite(finishedAtMs)) return "—";

  const pausedMs = pausedMsByScan[scan.scan_id] ?? 0;
  // Freeze at the pause start (independent of when this component mounted) while
  // the scan is actively paused.
  const ongoingPauseSinceMs =
    status === "paused" && activePause?.scanId === scan.scan_id ? activePause.since : null;
  return formatHms(
    elapsedActiveSeconds({
      startedAtMs,
      nowMs: now,
      pausedMs,
      ongoingPauseSinceMs,
      finishedAtMs: isFinished ? finishedAtMs : null,
    }),
  );
}
